import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Check, Languages } from "lucide-react";
import Flag from "@/components/Flag";
import { LANGUAGES, setLanguage } from "@/lib/i18n";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Topbar language picker.
 * - Visible list filtered by hotel.settings.languages (if set) ∩ user.allowed_langs (if any)
 * - Persists the choice to localStorage + (best-effort) /api/auth/preferences
 */
export default function LanguageSwitcher() {
    const { i18n } = useTranslation();
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [allowed, setAllowed] = useState(null);  // null = all
    const [coords, setCoords] = useState({ top: 0, right: 0 });
    const ref = useRef(null);
    const btnRef = useRef(null);

    useEffect(() => {
        function onDocClick(e) {
            if (!ref.current) return;
            if (ref.current.contains(e.target)) return;
            const portal = ref.current.__portal;
            if (portal && portal.contains(e.target)) return;
            setOpen(false);
        }
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    // Position the fixed dropdown right under the trigger button.
    useEffect(() => {
        if (!open) return;
        const update = () => {
            if (!btnRef.current) return;
            const r = btnRef.current.getBoundingClientRect();
            setCoords({ top: r.bottom + 4, right: Math.max(6, window.innerWidth - r.right) });
        };
        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
        };
    }, [open]);

    // Resolve which languages are enabled for this hotel.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!user || user.role === "super_admin") {
                setAllowed(null);
                return;
            }
            try {
                const { data } = await api.get("/i18n/available");
                if (!cancelled) setAllowed(data.languages || null);
            } catch {
                if (!cancelled) setAllowed(null);
            }
        })();
        return () => { cancelled = true; };
    }, [user]);

    const visible = allowed
        ? LANGUAGES.filter((l) => allowed.includes(l.code))
        : LANGUAGES;

    const current = visible.find((l) => l.code === i18n.language)
                 || LANGUAGES.find((l) => l.code === i18n.language)
                 || LANGUAGES[0];

    const choose = async (code) => {
        setLanguage(code);
        setOpen(false);
        try { await api.put("/auth/preferences", { language: code }); } catch { /* best-effort */ }
    };

    return (
        <div className="relative" ref={ref}>
            <button
                ref={btnRef}
                data-testid="lang-switcher-button"
                onClick={() => setOpen((s) => !s)}
                title={current.english}
                className="flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1.5 text-[12px] font-medium text-foreground hover:bg-muted/40"
            >
                <Flag country={current.country} />
                <span className="hidden md:inline">{current.label}</span>
                <Languages size={12} className="text-muted-foreground" />
            </button>
            {open && createPortal(
                <div
                    data-testid="lang-switcher-menu"
                    className="fixed z-[80] w-44 max-w-[calc(100vw-1rem)] rounded-md border border-border bg-card shadow-lg overflow-hidden"
                    style={{ top: coords.top, right: coords.right }}
                    ref={(el) => { if (ref.current) ref.current.__portal = el; }}
                >
                    {visible.map((l) => (
                        <button
                            key={l.code}
                            data-testid={`lang-option-${l.code}`}
                            onClick={() => choose(l.code)}
                            className={`flex w-full items-center gap-2 px-3 py-2 text-[13px] text-left hover:bg-muted/60 ${
                                l.code === current.code ? "bg-muted/40 text-foreground" : "text-muted-foreground"
                            }`}
                        >
                            <Flag country={l.country} />
                            <span className="flex-1">{l.label}</span>
                            {l.code === current.code && <Check size={12} className="text-[#ff7b02]" />}
                        </button>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
}
